import React from 'react';
import styled from 'styled-components';

const SMSTerms = () => {
  return (
    <PageContainer>
      <Section>
        <ContentWrapper>
          <PagePill>SMS Program</PagePill>

          <MainHeading>SMS Terms</MainHeading>

          <IntroText>
            These SMS Terms describe the text-message program operated by
            Sonorai and explain how consent, opt-out, and message frequency
            work. They apply only where you have voluntarily provided your
            phone number and chosen to opt in.
          </IntroText>

          <ContentSection>
            <SectionHeading>1. Program Name and Sender</SectionHeading>
            <InfoText>
              <strong>Program name:</strong> Sonorai SMS Communications<br />
              <strong>Sender:</strong> Elgazzar Ventures LLC, operating the
              Sonorai brand, 33 North Gould Street, Sheridan, WY 82801,
              United States.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>2. Types of Messages</SectionHeading>
            <BulletList>
              <li>Responses to inquiries you have submitted.</li>
              <li>Appointment confirmations and reminders.</li>
              <li>Service updates related to your engagement with us.</li>
              <li>Customer-support messages.</li>
              <li>Marketing messages about updates, offers, and services, only where you have given separate, explicit consent to marketing.</li>
            </BulletList>
            <InfoText>
              We collect consent for non-marketing messages and for marketing
              messages separately. Opting in to one does not opt you in to the
              other.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>3. Message Frequency</SectionHeading>
            <InfoText>
              Message frequency may vary based on your interactions with us
              and the program you opted in to.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>4. Costs</SectionHeading>
            <InfoText>
              Message and data rates may apply, depending on your mobile
              carrier and plan. Sonorai does not charge a separate fee to
              receive SMS from the program.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>5. Opt-Out and Help</SectionHeading>
            <InfoText>
              <strong>Opt-out:</strong> Reply <strong>STOP</strong> at any time
              to unsubscribe from the program. You will receive a confirmation
              message and will not receive further SMS from that program after
              that.
            </InfoText>
            <InfoText>
              <strong>Help:</strong> Reply <strong>HELP</strong> for assistance
              or contact us at{' '}
              <a href="mailto:mohamed@sonorai.co">mohamed@sonorai.co</a>.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>6. Consent Is Not a Condition of Purchase</SectionHeading>
            <InfoText>
              Opting in to receive SMS messages is voluntary. You are not
              required to consent to SMS in order to purchase products or
              services from us, or to receive a proposal.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>7. Carriers and Delivery</SectionHeading>
            <InfoText>
              We rely on third-party messaging providers and mobile carriers
              to deliver SMS. Carriers are not liable for delayed or
              undelivered messages. Delivery may also be affected by network
              conditions, your handset, and roaming status.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>8. How We Handle Your Phone Number</SectionHeading>
            <Highlight>
              SMS opt-in consent and phone numbers collected for SMS purposes
              will not be sold, rented, shared, or disclosed to third parties
              or affiliates for their own marketing purposes.
            </Highlight>
            <InfoText>
              We may share data with service providers that help us operate
              our website, CRM, communications, and SMS delivery, but only as
              needed to provide our services and subject to appropriate
              safeguards. Full details of how we process personal data are in
              our <a href="#privacy">Privacy Policy</a>.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>9. Eligibility</SectionHeading>
            <InfoText>
              The program is intended for individuals who are at least 18
              years old (or the age of majority in their jurisdiction) and who
              own or are authorized to use the mobile number provided.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>10. Changes to the Program</SectionHeading>
            <InfoText>
              We may modify or discontinue the SMS program at any time. We
              will post material changes here and update the "last updated"
              date below.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>11. Related Policies</SectionHeading>
            <InfoText>
              By opting in you also agree to our{' '}
              <a href="#privacy">Privacy Policy</a> and{' '}
              <a href="#terms">Terms &amp; Conditions</a>.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>12. Contact</SectionHeading>
            <InfoText>
              For any question about our SMS program, contact us at{' '}
              <a href="mailto:mohamed@sonorai.co">mohamed@sonorai.co</a>.
            </InfoText>
          </ContentSection>

          <LastUpdated>Company details updated: September 10, 2026</LastUpdated>
        </ContentWrapper>
      </Section>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: var(--bg-0);
`;

const Section = styled.section`
  padding: 120px 20px 100px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 780px;
  margin: 0 auto;
`;

const PagePill = styled.div`
  display: inline-block;
  padding: 6px 14px;
  background-color: var(--bg-2);
  border: 1px solid var(--line-2);
  border-radius: 9999px;
  color: var(--fg-2);
  font-family: var(--mono);
  font-size: 11px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.28px;
  margin-bottom: 24px;
  align-self: flex-start;
`;

const MainHeading = styled.h1`
  font-family: var(--display);
  font-size: 56px;
  font-weight: 400;
  letter-spacing: -1.6px;
  color: var(--fg-0);
  margin: 0 0 24px;
  line-height: 1.05;

  @media (max-width: 768px) {
    font-size: 40px;
    letter-spacing: -1px;
  }
`;

const IntroText = styled.p`
  font-family: var(--body);
  font-size: 17px;
  line-height: 1.55;
  color: var(--fg-2);
  margin: 0 0 48px;
  max-width: 680px;
`;

const ContentSection = styled.div`
  margin-bottom: 36px;
`;

const SectionHeading = styled.h2`
  font-family: var(--body);
  font-size: 18px;
  font-weight: 600;
  color: var(--fg-0);
  margin: 0 0 12px;
  letter-spacing: -0.2px;
`;

const InfoText = styled.p`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-1);
  margin: 0 0 12px;

  a { color: var(--blue); text-decoration: underline; }
  a:hover { color: var(--fg-0); }
`;

const BulletList = styled.ul`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-1);
  padding-left: 22px;
  margin: 4px 0 12px;

  li { margin: 6px 0; }
`;

const Highlight = styled.p`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-0);
  background: var(--bg-1);
  border-left: 3px solid var(--fg-0);
  padding: 16px 18px;
  margin: 12px 0;
  border-radius: 0 8px 8px 0;
`;

const LastUpdated = styled.p`
  font-family: var(--mono);
  font-size: 11px;
  color: var(--fg-3);
  text-transform: uppercase;
  letter-spacing: 0.28px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid var(--line-2);
`;

export default SMSTerms;
